<template>
  <div
    :key="title"
    class="field border-1 border-gray-300 p-2 border-round shadow-1"
  >
    <div class="flex m-0 p-0">
      <h3 class="text-gray-700 mb-0 pb-0 w-fit">{{ title }}</h3>
    </div>
    <p class="pb-3 m-0 text-xs pt-1">{{ help }}</p>

    <div>
      <div
        v-for="(config, i) in perkConfigs"
        :key="i"
        class="field border-1 p-2 border-gray-300 rounded shadow-1"
      >
        <div class="flex justify-between">
          <p class="font-medium">Perk {{ i + 1 }}</p>
          <Button
            @click="removeSignupPerk(i)"
            class="ml-auto h-2rem p-button-danger"
          >
            Remove
          </Button>
        </div>

        <Dropdown
          v-model="config.perkId.id"
          :options="perks"
          filter
          optionLabel="title"
          optionValue="id"
          :optionDisabled="(perk) => !perk.enabled"
          placeholder="Select Perk"
        />

        <label v-if="!config.isFromNightPay" class="mt-3">Venue</label>
        <Dropdown
          v-if="!config.isFromNightPay"
          v-model="config.venueId"
          :options="venues"
          filter
          optionLabel="name"
          optionValue="id"
          placeholder="All"
        />

        <label class="mt-3">Is from NightPay</label>
        <InputSwitch v-model="config.isFromNightPay" />

        <label class="mt-3">Quantity</label>
        <InputNumber v-model="config.quantity" style="width: 200px" />

        <label class="mt-3">Expires</label>
        <InputSwitch v-model="config.expires" />

        <div v-if="config.expires">
          <label class="mt-2 mr-2">Expires after</label>
          <InputNumber v-model="config.expire" />
          <Dropdown
            class="ml-1"
            v-model="config.expireType"
            :options="durationTypes"
            optionLabel="title"
            optionValue="id"
            placeholder="Select duration"
          />

          <div class="my-2">or</div>

          <label class="mt-2 mr-2"
            >Expires at date (overrules expires in)</label
          >
          <div class="flex align-items-center gap-2">
            <Calendar 
              v-model="config.expireDate" 
              placeholder="Select date" 
              class="flex-1"
            />
            <Button
              v-if="config.expireDate"
              @click="config.expireDate = null; emitUpdate()"
              icon="pi pi-times"
              class="p-button-outlined p-button-sm"
              v-tooltip="'Clear expiration date'"
            />
          </div>
        </div>

        <!-- Time-based Redemption Controls -->
        <div class="mt-4 p-3 border-1 border-gray-200 border-round">
          <h4 class="text-sm font-semibold mb-2">🕒 Redemption Time Control</h4>
          
          <div class="field mb-3">
            <label class="text-sm font-medium">Limit perk redemption hours</label>
            <div class="flex align-items-center mt-1">
              <InputSwitch 
                v-model="config.perkRedemptionTimeControl.isRedemptionTimeLimited" 
                @change="onRedemptionTimeLimitedChange(config)"
              />
              <span class="ml-2 text-xs text-gray-600">
                {{ config.perkRedemptionTimeControl.isRedemptionTimeLimited ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
            <small class="text-xs text-gray-500 mt-1">
              When enabled, perk redemption will be restricted to specific hours
            </small>
          </div>

          <div v-if="config.perkRedemptionTimeControl.isRedemptionTimeLimited" class="grid">
            <div class="col-12 md:col-6">
              <label class="text-sm font-medium">🔒 Locked from time</label>
              <Calendar
                v-model="config.perkRedemptionTimeControl.lockedFromTimeDate"
                timeOnly
                hourFormat="24"
                placeholder="e.g., 22:00"
                @date-select="onTimeChange(config, 'lockedFromTime', $event)"
                class="w-full mt-1"
                :inputStyle="{ cursor: 'pointer' }"
                @beforeinput.prevent
                @input.prevent
                @keypress.prevent
                @keydown.prevent
              />
              <small class="text-xs text-gray-500">
                Time when perk becomes unavailable for redemption in {{ detectedTimeZone }}
              </small>
            </div>

            <div class="col-12 md:col-6">
              <label class="text-sm font-medium">🔓 Unlocked at time</label>
              <Calendar
                v-model="config.perkRedemptionTimeControl.unlockedAtTimeDate"
                timeOnly
                hourFormat="24"
                placeholder="e.g., 10:00"
                @date-select="onTimeChange(config, 'unlockedAtTime', $event)"
                class="w-full mt-1"
                :inputStyle="{ cursor: 'pointer' }"
                @beforeinput.prevent
                @input.prevent
                @keypress.prevent
                @keydown.prevent
              />
              <small class="text-xs text-gray-500">
                Time when perk becomes available again in {{ detectedTimeZone }}
              </small>
            </div>

            <div class="col-12 mt-2">
              <label class="text-sm font-medium">🌍 Timezone</label>
              <InputText
                v-model="config.perkRedemptionTimeControl.timeZoneId"
                readonly
                class="w-full mt-1"
              />
              <small class="text-xs text-gray-500">
                Timezone for the redemption time control (auto-detected)
              </small>
            </div>
          </div>
        </div>
      </div>
      <Button @click="addSignupPerk">Add Perk</Button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";

export default {
  props: {
    perkConfigs: Array,
    title: String,
    help: String,
  },
  emits: ["update:perkConfigs"],
  setup(props, { emit }) {
    const store = useStore();
    const perks = ref([]);
    const venues = ref([]);
    const durationTypes = ref([
      { id: "Day", title: "day(s)" },
      { id: "Week", title: "week(s)" },
      { id: "Month", title: "month(s)" },
      { id: "Year", title: "year(s)" },
    ]);

    // Get the user's timezone
    const detectedTimeZone = computed(() => {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    });

    // Get current local time for display
    const currentLocalTime = computed(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    });

    function loadRequiredData() {
      store.dispatch("perks/ensurePerkListItems").then(() => {
        perks.value = store.getters["perks/perkListItems"];
      });

      store.dispatch("venues/ensureVenueListItems").then(() => {
        venues.value = [
          { id: null, name: "All" },
          ...store.getters["venues/venueListItems"],
        ];
      });
    }

    function initializeRedemptionTimeControl(config) {
      if (!config.perkRedemptionTimeControl) {
        config.perkRedemptionTimeControl = {
          isRedemptionTimeLimited: false,
          lockedFromTime: null,
          unlockedAtTime: null,
          lockedFromTimeDate: null,
          unlockedAtTimeDate: null,
          timeZoneId: detectedTimeZone.value
        };
      } else {
        // Initialize Date objects for time pickers from string times
        if (config.perkRedemptionTimeControl.lockedFromTime) {
          config.perkRedemptionTimeControl.lockedFromTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.lockedFromTime);
        }
        if (config.perkRedemptionTimeControl.unlockedAtTime) {
          config.perkRedemptionTimeControl.unlockedAtTimeDate = parseTimeToDate(config.perkRedemptionTimeControl.unlockedAtTime);
        }
        // Ensure timezone is set
        if (!config.perkRedemptionTimeControl.timeZoneId) {
          config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
        }
      }
    }

    function parseTimeToDate(timeString) {
      if (!timeString) return null;
      const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
      
      // Create a date object in local time
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      return date;
    }

    function formatTimeFromDate(date) {
      if (!date) return null;
      
      // Get time in local timezone
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = "00"; // Always use 00 for seconds
      
      return `${hours}:${minutes}:${seconds}`; // TimeSpan format for JSON.NET
    }

    function onRedemptionTimeLimitedChange(config) {
      if (!config.perkRedemptionTimeControl.isRedemptionTimeLimited) {
        // Clear time restrictions when disabled
        config.perkRedemptionTimeControl.lockedFromTime = null;
        config.perkRedemptionTimeControl.lockedFromTimeDate = null;
        config.perkRedemptionTimeControl.unlockedAtTime = null;
        config.perkRedemptionTimeControl.unlockedAtTimeDate = null;
      } else {
        // Set default timezone when enabling
        config.perkRedemptionTimeControl.timeZoneId = detectedTimeZone.value;
      }
      emitUpdate();
    }

    function onTimeChange(config, timeType, event) {
      const timeString = formatTimeFromDate(event);
      if (timeType === 'lockedFromTime') {
        config.perkRedemptionTimeControl.lockedFromTime = timeString;
      } else if (timeType === 'unlockedAtTime') {
        config.perkRedemptionTimeControl.unlockedAtTime = timeString;
      }
      emitUpdate();
    }

    function addSignupPerk() {
      const updatedPerks = [...props.perkConfigs];
      const newPerk = {
        perkId: { id: "" },
        venueId: undefined,
        isFromNightPay: false,
        quantity: 1,
        expires: false,
        expire: 1,
        expireType: "Month",
        perkRedemptionTimeControl: {
          isRedemptionTimeLimited: false,
          lockedFromTime: null,
          unlockedAtTime: null,
          lockedFromTimeDate: null,
          unlockedAtTimeDate: null,
          timeZoneId: detectedTimeZone.value
        }
      };
      updatedPerks.push(newPerk);
      emit("update:perkConfigs", updatedPerks);
    }

    function removeSignupPerk(index) {
      const updatedPerks = [...props.perkConfigs];
      updatedPerks.splice(index, 1);
      emit("update:perkConfigs", updatedPerks);
    }

    function emitUpdate() {
      emit("update:perkConfigs", props.perkConfigs);
    }

    onMounted(() => {
      loadRequiredData();
      // Initialize redemption time control for existing configs
      props.perkConfigs?.forEach(config => {
        initializeRedemptionTimeControl(config);
      });
    });

    return {
      perks,
      venues,
      durationTypes,
      addSignupPerk,
      removeSignupPerk,
      onRedemptionTimeLimitedChange,
      onTimeChange,
      detectedTimeZone,
      currentLocalTime
    };
  },
};
</script>

<style scoped>
.grid {
  display: grid;
  gap: 1rem;
}

.col-12 {
  grid-column: span 12;
}

.md\:col-6 {
  grid-column: span 6;
}

@media (max-width: 768px) {
  .md\:col-6 {
    grid-column: span 12;
  }
}
</style>
